import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { API_BASE, BACKEND_BASE } from '../config/api';
import useCart from '../hooks/useCart';
import './OrderConfirmationPage.css';

const getAbsoluteProductImageUrl = (imagePath) => {
  if (!imagePath) return '';

  if (/^data:/i.test(imagePath) || /^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  if (imagePath.startsWith('//')) {
    return `${window.location.protocol}${imagePath}`;
  }

  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return new URL(normalizedPath, BACKEND_BASE).toString();
};

const blobToPngDataUrl = (blob) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(blob);
  const image = new Image();

  image.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(error);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to load product image'));
  };

  image.src = objectUrl;
});

const loadInvoiceImageData = async (imagePath) => {
  const imageUrl = getAbsoluteProductImageUrl(imagePath);
  if (!imageUrl) return null;

  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Unable to fetch image: ${response.status}`);
  }

  const blob = await response.blob();
  return blobToPngDataUrl(blob);
};

const getPaymentMethodLabel = (paymentMethod = '') => {
  const normalizedMethod = String(paymentMethod || '').trim().toLowerCase();

  if (normalizedMethod === 'online' || normalizedMethod === 'upi' || normalizedMethod === 'razorpay') {
    return 'Online Payment';
  }

  return normalizedMethod ? 'Offline Payment' : 'Online Payment';
};

const normalizePaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    return null;
  }

  if (typeof paymentMethod === 'string') {
    return {
      id: paymentMethod,
      name: getPaymentMethodLabel(paymentMethod)
    };
  }

  const methodId = paymentMethod.id || paymentMethod.paymentMethod || '';

  return {
    ...paymentMethod,
    id: methodId,
    name: getPaymentMethodLabel(methodId)
  };
};

const isAwaitingOnlinePayment = (orderData) =>
  orderData?.orderStatus === 'Processing' || orderData?.orderStatus === 'Order Placed';

const OrderConfirmationPage = () => {
  const [order, setOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTitle, setLoadingTitle] = useState('Confirming Your Payment');
  const [loadingMessage, setLoadingMessage] = useState('Please wait while we verify your payment and prepare your order confirmation.');
  const [orderStatus, setOrderStatus] = useState('processing');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [error, setError] = useState(''); // Add missing error state
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();

  const redirectToPaymentFailure = (paymentState = 'failed', message = '') => {
    navigate('/payment-failed', {
      replace: true,
      state: {
        paymentState,
        message: message || (
          paymentState === 'incomplete'
            ? 'Payment not completed. Please proceed again.'
            : 'Payment failed. Please try again.'
        )
      }
    });
  };

  const getPaymentFailureState = (orderData) => {
    const paymentState = orderData?.paymentResult?.failure_type === 'incomplete' ||
      orderData?.orderStatus === 'Payment Not Completed'
      ? 'incomplete'
      : 'failed';

    return {
      paymentState,
      message: orderData?.paymentResult?.failure_reason || (
        paymentState === 'incomplete'
          ? 'Payment not completed. Please proceed again.'
          : 'Payment failed. Please try again.'
      )
    };
  };

  useEffect(() => {
    let retryTimeout = null;
    let isMounted = true;

    const fetchOrderDetails = async () => {
      let keepLoadingForPaymentVerification = false;
      setLoading(true);
      try {
        // Check if we have order data from navigation state
        if (location.state?.order) {
          console.log('Order data found in location state:', location.state.order);
          const orderFromState = location.state.order;
          
          // Check if the order has failed payment status
          if (orderFromState.orderStatus === 'Payment Failed' ||
              orderFromState.orderStatus === 'Payment Not Completed' ||
              orderFromState.paymentResult?.status === 'FAILED' ||
              (orderFromState.paymentMethod === 'online' && !orderFromState.isPaid && !isAwaitingOnlinePayment(orderFromState))) {
            console.error('Order in location state indicates payment was not completed successfully');
            const failureState = getPaymentFailureState(orderFromState);
            redirectToPaymentFailure(failureState.paymentState, failureState.message);
            return;
          }
          
          setOrder(orderFromState);
          if (location.state.paymentMethod) {
            setPaymentMethod(normalizePaymentMethod(location.state.paymentMethod));
          } else {
            // Fallback to determining payment method from order data
            setPaymentMethod(normalizePaymentMethod(orderFromState.paymentMethod));
          }
        } else {
          // Get order data from sessionStorage
          const savedOrder = sessionStorage.getItem('lastOrder');
          const savedPaymentMethod = sessionStorage.getItem('lastPaymentMethod');
          
          if (savedOrder) {
            const parsedOrder = JSON.parse(savedOrder);
            
            // Check if the order has failed payment status
            if (parsedOrder.orderStatus === 'Payment Failed' ||
                parsedOrder.orderStatus === 'Payment Not Completed' ||
                parsedOrder.paymentResult?.status === 'FAILED' ||
                (parsedOrder.paymentMethod === 'online' && !parsedOrder.isPaid && !isAwaitingOnlinePayment(parsedOrder))) {
              console.error('Order in sessionStorage indicates payment was not completed successfully');
              sessionStorage.removeItem('lastOrder');
              sessionStorage.removeItem('lastPaymentMethod');
              const failureState = getPaymentFailureState(parsedOrder);
              redirectToPaymentFailure(failureState.paymentState, failureState.message);
              return;
            }
            
            setOrder(parsedOrder);
            
            if (savedPaymentMethod) {
              setPaymentMethod(normalizePaymentMethod(JSON.parse(savedPaymentMethod)));
            } else {
              // Fallback to determining payment method from order data
              setPaymentMethod(normalizePaymentMethod(parsedOrder.paymentMethod));
            }
          } else {
            // Check for payment failure indicators in URL
            const paymentStatus = searchParams.get('payment_status');
            const paymentFailed = searchParams.get('payment_failed');
            
            if (paymentStatus === 'failed' || paymentStatus === 'incomplete' || paymentFailed === 'true') {
              console.error('Payment failure detected in URL parameters');
              keepLoadingForPaymentVerification = true;
              setLoadingTitle('Checking Payment Status');
              setLoadingMessage('Please wait while we read the payment response from the gateway.');
              sessionStorage.removeItem('lastOrder');
              sessionStorage.removeItem('lastPaymentMethod');
              retryTimeout = setTimeout(() => {
                redirectToPaymentFailure(
                  paymentStatus === 'incomplete' ? 'incomplete' : 'failed',
                  paymentStatus === 'incomplete'
                    ? 'Payment not completed. Please proceed again.'
                    : 'Payment failed. Please try again.'
                );
              }, 900);
              return;
            }
            
            // If we have a local order ID in the URL, fetch the order details
            if (searchParams.get('id')) {
              console.log('Fetching order details for ID:', searchParams.get('id'));
              try {
                const { data } = await axios.post(
                  `${API_BASE}/v1/order/${searchParams.get('id')}`,
                  { withCredentials: true }
                );
                
                if (data.success) {
                  setOrder(data.order);
                  setPaymentMethod(normalizePaymentMethod(data.order.paymentMethod));
                } else {
                  setError('Failed to fetch order details');
                }
              } catch (fetchError) {
                console.error('Error fetching order details:', fetchError);
                setError('Failed to fetch order details');
              }
            } else {
              console.log('No order data found in state, sessionStorage, or query params');
              // No order data available, redirect to home after a delay
              setTimeout(() => {
                navigate('/');
              }, 5000);
            }
          }
        }

        // Clear the cart regardless of how we got the order data (COD or online payment)
        clearCart();

        // Set estimated delivery date (5-7 days from now)
        const deliveryDate = new Date();
        deliveryDate.setDate(deliveryDate.getDate() + 5 + Math.floor(Math.random() * 3));
        
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        setEstimatedDelivery(deliveryDate.toLocaleDateString('en-US', options));
        
        // Simulate order processing
        const timer = setTimeout(() => {
          setOrderStatus('confirmed');
        }, 3000);
        
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error in order details processing:', error);
        setError('An error occurred while processing your order details');
      } finally {
        if (!keepLoadingForPaymentVerification && isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchOrderDetails();
    return () => {
      isMounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [location, navigate, searchParams]);

  // Ensure orderStatus is set to 'confirmed' when order is loaded
  useEffect(() => {
    if (order && orderStatus !== 'confirmed') {
      const timer = setTimeout(() => {
        setOrderStatus('confirmed');
      }, 1000); // 1 second delay for smooth transition
      return () => clearTimeout(timer);
    }
  }, [order]);

  // Clear lastOrder and lastPaymentMethod from sessionStorage after displaying the order
  useEffect(() => {
    if (order) {
      sessionStorage.removeItem('lastOrder');
      sessionStorage.removeItem('lastPaymentMethod');
    }
  }, [order]);

  // Defensive PDF generation for invoice
  const generateInvoicePDF = () => {
    try {
      if (!order) throw new Error('Order data not available');
      if (!order.orderItems || !Array.isArray(order.orderItems) || order.orderItems.length === 0) throw new Error('Order items missing');
      if (!order.shippingAddress) throw new Error('Shipping address missing');
      if (!order.user) throw new Error('User info missing');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add company logo/header
      doc.setFontSize(20);
      doc.setTextColor(44, 62, 80);
      doc.text('PANCHALI JEWELS', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('Invoice / Payment Receipt', pageWidth / 2, 30, { align: 'center' });
      
      // Add a line
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 35, pageWidth - 20, 35);
      
      // Order details
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(`Order Number: ${order._id}`, 20, 45);
      doc.text(`Order Date: ${new Date(order.createdAt).toLocaleDateString()}`, 20, 52);
      doc.text(`Payment Method: ${paymentMethod ? paymentMethod.name : 'N/A'}`, 20, 59);
      
      // Customer details
      doc.text('Shipping Address:', pageWidth - 80, 45);
      doc.text(`${order.shippingAddress.fullName}`, pageWidth - 80, 52);
      doc.text(`${order.shippingAddress.addressLine1}`, pageWidth - 80, 59);
      if (order.shippingAddress.addressLine2) {
        doc.text(`${order.shippingAddress.addressLine2}`, pageWidth - 80, 66);
      }
      doc.text(`${order.shippingAddress.city}, ${order.shippingAddress.state} - ${order.shippingAddress.pincode}`, pageWidth - 80, order.shippingAddress.addressLine2 ? 73 : 66);
      
      // Add a line
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 80, pageWidth - 20, 80);
      
      // Order items table
      const tableColumn = ["Item", "Quantity", "Price", "Total"];
      const tableRows = [];
      
      order.orderItems.forEach(item => {
        const itemData = [
          item.title,
          item.quantity,
          `₹${item.price.toFixed(2)}`,
          `₹${(item.price * item.quantity).toFixed(2)}`
        ];
        tableRows.push(itemData);
      });
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 85,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] }
      });
      
      // Order summary
      const finalY = doc.lastAutoTable.finalY + 10;
      
      doc.text('Order Summary', 120, finalY);
      doc.text(`Subtotal: ₹${order.itemsPrice.toFixed(2)}`, 120, finalY + 7);
      doc.text('Shipping: FREE', 120, finalY + 14);
      if (order.taxPrice > 0) {
        doc.text(`Tax: ₹${order.taxPrice.toFixed(2)}`, 120, finalY + 21);
      }
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Total: ₹${order.totalPrice.toFixed(2)}`, 120, finalY + (order.taxPrice > 0 ? 28 : 21));
      
      // Footer
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for shopping with us!', pageWidth / 2, doc.internal.pageSize.getHeight() - 20, { align: 'center' });
      
      // Save the PDF
      doc.save(`Invoice_${order._id}.pdf`);
    } catch (err) {
      alert('Unable to generate invoice: ' + (err.message || 'Unknown error'));
    }
  };

  const handleInvoiceDownload = async () => {
    try {
      if (!order) throw new Error('Order data not available');

      const orderItems = Array.isArray(order.orderItems) ? order.orderItems : [];
      if (!orderItems.length) throw new Error('Order items missing');

      const shippingAddress = order.shippingAddress || {};
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const invoiceNumber = order._id || order.razorpayOrderId || order.paymentResult?.id || `PJ-${Date.now()}`;
      const rawOrderDate = order.createdAt || order.paidAt || new Date().toISOString();
      const parsedOrderDate = new Date(rawOrderDate);
      const safeOrderDate = Number.isNaN(parsedOrderDate.getTime()) ? new Date() : parsedOrderDate;
      const paymentMethodName = paymentMethod?.name || getPaymentMethodLabel(order.paymentMethod);
      const formatCurrency = (value) => `Rs. ${(Number(value) || 0).toFixed(2)}`;
      const subtotal = Number.isFinite(Number(order.itemsPrice))
        ? Number(order.itemsPrice)
        : orderItems.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
      const shippingPrice = Number(order.shippingPrice) || 0;
      const taxPrice = Number(order.taxPrice) || 0;
      const totalPrice = Number.isFinite(Number(order.totalPrice)) && Number(order.totalPrice) > 0
        ? Number(order.totalPrice)
        : subtotal + shippingPrice + taxPrice;
      const invoiceItems = await Promise.all(
        orderItems.map(async (item) => {
          let imageDataUrl = null;

          try {
            imageDataUrl = await loadInvoiceImageData(item.image);
          } catch (imageError) {
            console.warn('Unable to load invoice product image:', item.image, imageError);
          }

          return {
            ...item,
            quantity: Number(item.quantity) || 1,
            unitPrice: Number(item.price) || 0,
            imageDataUrl
          };
        })
      );

      const addressLines = [
        shippingAddress.fullName,
        shippingAddress.addressLine1,
        shippingAddress.addressLine2,
        [
          [shippingAddress.city, shippingAddress.state].filter(Boolean).join(', '),
          shippingAddress.pincode ? `- ${shippingAddress.pincode}` : ''
        ].filter(Boolean).join(' '),
        shippingAddress.phone ? `Phone: ${shippingAddress.phone}` : ''
      ].filter(Boolean);

      doc.setFontSize(20);
      doc.setTextColor(44, 62, 80);
      doc.text('PANCHALI JEWELS', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('Invoice / Payment Receipt', pageWidth / 2, 30, { align: 'center' });

      doc.setDrawColor(200, 200, 200);
      doc.line(20, 35, pageWidth - 20, 35);

      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(`Order Number: ${invoiceNumber}`, 20, 45);
      doc.text(`Order Date: ${safeOrderDate.toLocaleDateString()}`, 20, 52);
      doc.text(`Payment Method: ${paymentMethodName}`, 20, 59);

      doc.text('Shipping Address:', pageWidth - 80, 45);
      let currentAddressY = 52;
      addressLines.forEach((line) => {
        const wrappedLines = doc.splitTextToSize(line, 60);
        doc.text(wrappedLines, pageWidth - 80, currentAddressY);
        currentAddressY += wrappedLines.length * 7;
      });

      const tableStartY = Math.max(90, currentAddressY + 8);
      doc.setDrawColor(200, 200, 200);
      doc.line(20, tableStartY - 5, pageWidth - 20, tableStartY - 5);

      const tableColumn = ['Image', 'Item', 'Quantity', 'Price', 'Total'];
      const tableRows = invoiceItems.map((item) => {
        return [
          '',
          item.title || item.name || 'Jewelry Item',
          item.quantity,
          formatCurrency(item.unitPrice),
          formatCurrency(item.unitPrice * item.quantity)
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: tableStartY,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          minCellHeight: 24,
          valign: 'middle'
        },
        columnStyles: {
          0: { cellWidth: 24, halign: 'center' },
          1: { cellWidth: 58 }
        },
        headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        didDrawCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 0) {
            return;
          }

          const rowItem = invoiceItems[data.row.index];
          const imageX = data.cell.x + 3;
          const imageY = data.cell.y + 3;
          const imageSize = 18;

          if (rowItem?.imageDataUrl) {
            doc.addImage(rowItem.imageDataUrl, 'PNG', imageX, imageY, imageSize, imageSize);
            return;
          }

          doc.setFontSize(7);
          doc.setTextColor(130, 130, 130);
          doc.text('No image', data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, {
            align: 'center',
            baseline: 'middle'
          });
          doc.setTextColor(60, 60, 60);
          doc.setFontSize(9);
        }
      });

      let summaryY = (doc.lastAutoTable?.finalY || tableStartY) + 12;
      if (summaryY > pageHeight - 45) {
        doc.addPage();
        summaryY = 20;
      }

      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text('Order Summary', 120, summaryY);
      doc.text(`Subtotal: ${formatCurrency(subtotal)}`, 120, summaryY + 8);
      doc.text('Shipping: FREE', 120, summaryY + 16);
      if (taxPrice > 0) {
        doc.text(`Tax: ${formatCurrency(taxPrice)}`, 120, summaryY + 24);
      }

      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(
        `Total: ${formatCurrency(totalPrice)}`,
        120,
        summaryY + (taxPrice > 0 ? 32 : 24)
      );

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for shopping with us!', pageWidth / 2, pageHeight - 20, { align: 'center' });

      doc.save(`Invoice_${String(invoiceNumber).replace(/[^\w-]+/g, '_')}.pdf`);
    } catch (err) {
      alert('Unable to generate invoice: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="processing-order processing-order--payment">
            <div className="confirmation-loader" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <h2>{loadingTitle}</h2>
            <p>{loadingMessage}</p>
            <small>We will automatically show your order confirmation or the payment failed page.</small>
          </div>
        </div>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="error-message">
            <h2>Order Information Not Available</h2>
            <p>We couldn't find your order information. Please check your orders in your account.</p>
            <p>If payment was deducted, please contact support with your payment reference.</p>
            {error && <p className="error-text">{error}</p>} {/* Display error message */}
            <div className="order-actions">
              <Link to="/account" className="account-link">Go to My Account</Link>
              <Link to="/" className="continue-shopping">Return to Home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">
        {orderStatus === 'processing' ? (
          <div className="processing-order">
            <div className="loader"></div>
            <h2>Processing Your Order</h2>
            <p>Please wait while we confirm your order...</p>
          </div>
        ) : (
          <>
            <div className="order-success">
              <div className="success-icon">✓</div>
              <h1>Thank You for Your Order!</h1>
              <p className="success-message">Your order has been placed successfully and is being processed.</p>
              <div className="order-number">
                <span>Order Number:</span> #{order._id}
              </div>
              <p className="confirmation-email">A confirmation email has been sent to your registered email address.</p>
            </div>
            
            <div className="order-details-card">
              <div className="card-header">
                <h2>Order Details</h2>
                <button onClick={handleInvoiceDownload} className="download-invoice-btn">
                  <span className="invoice-icon">📄</span> Download Invoice
                </button>
              </div>
              
              <div className="order-info-grid">
                <div className="order-info-column">
                  <div className="info-group">
                    <h3>Shipping Address</h3>
                    <p>{order.shippingAddress.fullName}</p>
                    <p>{order.shippingAddress.addressLine1}</p>
                    {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                    <p>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</p>
                    <p>Phone: {order.shippingAddress.phone}</p>
                  </div>
                  
                  <div className="info-group">
                    <h3>Payment Information</h3>
                    <p><strong>Method:</strong> {paymentMethod ? paymentMethod.name : 'Online Payment'}</p>
                    <p><strong>Status:</strong> {!order.isPaid ? (
                      <span className="payment-status pending">Pending</span>
                    ) : (
                      <span className="payment-status paid">Paid</span>
                    )}</p>
                    <p><strong>Date:</strong> {new Date(order.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}</p>
                  </div>
                </div>
                
                <div className="order-info-column">
                  <div className="info-group">
                    <h3>Order Summary</h3>
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>₹{order.itemsPrice.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping:</span>
                      <span>FREE</span>
                    </div>
                    {order.taxPrice > 0 && (
                      <div className="summary-row">
                        <span>Tax:</span>
                        <span>₹{order.taxPrice.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>₹{order.totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="info-group">
                    <h3>Delivery Information</h3>
                    <p><strong>Estimated Delivery:</strong> {estimatedDelivery}</p>
                    <p><strong>Status:</strong> <span className="order-status processing">Processing</span></p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-items-card">
              <h2>Order Items</h2>
              <div className="order-items-list">
                {order.orderItems && order.orderItems.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-image">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="item-details">
                      <h4>{item.title}</h4>
                      <div className="item-meta">
                        <span className="item-quantity">Qty: {item.quantity}</span>
                        {item.selectedSize ? <span className="item-size">Size: {item.selectedSize}</span> : null}
                        <span className="item-price">₹{item.price.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="item-total">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-actions-card">
              <Link to="/" className="action-button home">
                <span className="action-icon">🏠</span> Continue Shopping
              </Link>
              <Link to={`/order/${order._id}`} className="action-button track">
                <span className="action-icon">📦</span> Track Order
              </Link>
              <Link to="/account" className="action-button account">
                <span className="action-icon">👤</span> My Account
              </Link>
            </div>
            
            <div className="customer-support">
              <h3>Need Help?</h3>
              <p>If you have any questions about your order, please contact our customer support.</p>
              <a href="mailto:support@panchalijewels.com" className="support-link">support@panchalijewels.com</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
